import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import Product from '@src/quince-site-common/src/domains/Product'
import { generateEDDLabel } from '@src/quince-site-common/src/hooks/edd/utils/edd.utils'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import { getCurrentFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

// eslint-disable-next-line import/no-named-as-default
import ZipCodeButton from '../zipCodeButton'

import styles from './eddMessageContainer.module.scss'
import OrderCutoffTime from './orderCutOffTime'

interface EDDMessageContainerProps {
  eddDetail: EDDDetails | undefined
  product: Product | undefined
  selectedInternalVariantId: number[] | undefined
  isQuickATCForMobile?: boolean
  showNewPdpUI?: boolean
}

interface ParsedDateResult {
  before: string
  date: string
  after: string
}

export const parseDateFromLabel = (label: string): ParsedDateResult => {
  const dateRegex = /\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s\d{1,2}\b/
  const match = label.match(dateRegex)
  const date = match?.[0] ?? label
  const parts = label.split(date)
  const before = parts[0] ?? ''
  const after = parts[1] ?? ''
  return { before, date, after }
}

export function EDDMessageContainer({
  eddDetail,
  product,
  selectedInternalVariantId,
  showNewPdpUI,
  isQuickATCForMobile,
}: EDDMessageContainerProps) {
  const { zipCode } = useZipCode()
  const selectedVariant = selectedInternalVariantId?.[0]?.toString()

  const { experimentBucket } = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const isEDDExpEnabled = experimentBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.EDD].buckets?.control

  const { t } = useTranslation('common', { keyPrefix: 'edd' })
  const { t: tCommon } = useTranslation('common')

  useEffect(() => {
    if (eddDetail?.showEDD) {
      const currentFindingMethod = getCurrentFindingMethod()
      trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
        // @i18n-ignore
        action: 'product_page_edd',
        // @i18n-ignore
        category: 'edd',
        context: {
          product_id: product?.spid,
          findingMethodAggregate: currentFindingMethod?.findingMethodAggregate,
          findingMethodGranular: currentFindingMethod?.findingMethodGranular,
          findingMethodId: currentFindingMethod?.findingMethodId,
          edd_standard_eligible: eddDetail.type === 'LB_STANDARD',
          edd_express_eligible: eddDetail.type === 'LB_SUPER',
          edd_max_days: eddDetail.max_days,
          edd_min_days: eddDetail.min_days,
          edd_max_date: eddDetail.max_date,
          edd_min_date: eddDetail.min_date,
          location_ids: eddDetail.inventoryLocations,
          productProperties: product && getProductPropertiesForTracking(product, selectedVariant),
          edd_label: generateEDDLabel(eddDetail, tCommon),
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eddDetail?.showEDD,
    eddDetail?.type,
    eddDetail?.max_days,
    eddDetail?.min_days,
    product?.id,
    selectedVariant,
    eddDetail?.max_date,
    eddDetail?.min_date,
    eddDetail?.inventoryLocations,
  ])

  if (!isEDDExpEnabled || eddDetail?.showEDD === undefined) return null

  if (!zipCode) {
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          showNewPdpUI && styles.eddMessageWrapper_adjustedPadding,
        )}
      >
        <div>
          {t('in_stock_and_ships_free_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{t('zip_code')}</ZipCodeButton>
        </div>
      </div>
    )
  }

  if (!eddDetail.showEDD && zipCode)
    return (
      <div className={classnames(styles.eddMessageWrapper, showNewPdpUI && styles.eddMessageWrapper_adjustedPadding)}>
        <div>
          {t('in_stock_and_ships_free_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )

  if (eddDetail.isExpressShipping) {
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          showNewPdpUI && styles.eddMessageWrapper_adjustedPadding,
        )}
      >
        <div>
          {generateEDDLabel(eddDetail, tCommon)} {tCommon('with_priority_shipping_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )
  }

  if (eddDetail.type === 'LB_STANDARD_PREMIUM' && eddDetail.max_cal_days === 2) {
    const label = generateEDDLabel(eddDetail, tCommon) ?? ''
    const { before, date, after } = parseDateFromLabel(label)
    return (
      <div className={classnames(styles.eddMessageWrapper, isQuickATCForMobile && styles.eddMessageWrapper__quickATC)}>
        <div className={styles.eddMessageWrapper__callOut}>
          <div>
            {before}
            <strong className={styles.eddMessageWrapper__bold}>{date}</strong>
            {after}
          </div>
          {eddDetail.cutoffDate && <OrderCutoffTime cutoffDate={eddDetail.cutoffDate} />}
        </div>
        <div>
          {t('in_stock_and_ships_free_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )
  }

  return (
    <div
      className={classnames(
        styles.eddMessageWrapper,
        isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
        showNewPdpUI && styles.eddMessageWrapper_adjustedPadding,
      )}
    >
      <div>
        {generateEDDLabel(eddDetail, tCommon)} {tCommon('to')}{' '}
        <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
      </div>
      {eddDetail.cutoffDate && <OrderCutoffTime cutoffDate={eddDetail.cutoffDate} />}
    </div>
  )
}
